// src/features/locations/locationsSlice.js
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { URL_LOCATIONS } from '../service/endpoints';
import { deleteData, getData, patchData, postData } from '../service/APIClient';

// Thunk to fetch a single location by ID
export const fetchLocationById = createAsyncThunk(
  'locations/fetchLocationById',
  async (id, thunkAPI) => {
    try {
      return await getData(`${URL_LOCATIONS}/${id}`);
    } catch (error) {
      console.error('Error fetching location:', error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to fetch all locations
export const fetchLocations = createAsyncThunk(
  'locations/fetchLocations',
  async (_, thunkAPI) => {
    let locations = [];
    let currentPage = 1;
    let totalPages = 1;

    try {
      while (currentPage <= totalPages) {
        const response = await getData(`${URL_LOCATIONS}?page=${currentPage}`);
        if (Array.isArray(response.data)) {
          locations = [...locations, ...response.data];
        } else {
          console.error('Expected data to be an array', response.data);
          break;
        }

        totalPages = response.totalPages;
        currentPage++;
      }

      return locations;
    } catch (error) {
      console.error('Error fetching locations:', error);
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Thunk to add a new location
export const addLocation = createAsyncThunk(
  'locations/addLocation',
  async (newLocation, thunkAPI) => {
    try {
      return await postData(URL_LOCATIONS, newLocation);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Delete a location
export const deleteLocation = createAsyncThunk(
  'locations/deleteLocation',
  async (locationId, thunkAPI) => {
    try {
      await deleteData(`${URL_LOCATIONS}/${locationId}`);
      return locationId; // Return the ID to remove it from the state
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const patchLocation = createAsyncThunk(
  'locations/patchLocation',
  async ({ locationId, updateData }, thunkAPI) => {
    try {
      const response = await patchData(`${URL_LOCATIONS}/${locationId}`, updateData);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

const locationsSlice = createSlice({
  name: 'locations',
  initialState: {
    data: [],
    status: 'idle',
    addStatus: 'idle',
    deleteStatus: 'idle',
    error: null,
    isModalVisible: false,
    currentLocation: null,
    currentLocationStatus: 'idle',
    currentLocationError: null,

  },
  reducers: {
    openModal: state => {
      state.isModalVisible = true;
    },
    closeModal: state => {
      state.isModalVisible = false;
    },
    resetAddStatus: state => {
      state.addStatus = 'idle';
    },
    setCurrentLocation: (state, action) => {
      state.currentLocation = action.payload;
    },
    clearCurrentLocation: state => {
      state.currentLocation = null;
      state.currentLocationStatus = 'idle';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchLocations.pending, state => {
        state.status = 'loading';
      })
      .addCase(fetchLocations.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.data = action.payload;
      })
      .addCase(fetchLocations.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(patchLocation.pending, state => {
        state.status = 'loading';
      })
      .addCase(patchLocation.fulfilled, (state, action) => {
        state.status = 'succeeded';
        // const updatedLocation = action.payload;
        // console.log(updatedLocation)
        //
        // const index = state.data.findIndex(location => location._id === updatedLocation._id);
        // if (index !== -1) {
        //   state.data[index] = updatedLocation;
        //   state.data.push(updatedLocation);
        // }
        // if (state.currentLocation && state.currentLocation._id === updatedLocation._id) {
        //   state.currentLocation = updatedLocation;
        // }
      })
      .addCase(patchLocation.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(addLocation.pending, state => {
        state.addStatus = 'loading';
      })
      .addCase(addLocation.fulfilled, (state, action) => {
        state.addStatus = 'succeeded';
        state.isModalVisible = false;
        state.data.push(action.payload);
      })
      .addCase(addLocation.rejected, (state, action) => {
        state.addStatus = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(deleteLocation.pending, state => {
        state.deleteStatus = 'loading';
      })
      .addCase(deleteLocation.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.data = state.data.filter(
          location => location._id !== action.payload
        );
      })
      .addCase(deleteLocation.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.error = action.payload || action.error.message;
      })
      .addCase(fetchLocationById.pending, state => {
        state.currentLocationStatus = 'loading';
        state.currentLocationError = null;
      })
      .addCase(fetchLocationById.fulfilled, (state, action) => {
        state.currentLocationStatus = 'succeeded';
        state.currentLocation = action.payload;
      })
      .addCase(fetchLocationById.rejected, (state, action) => {
        state.currentLocationStatus = 'failed';
        state.currentLocationError = action.payload || action.error.message;
      });
  }
});

export const {
  openModal,
  closeModal,
  resetAddStatus,
  clearCurrentLocation
} = locationsSlice.actions;

export default locationsSlice.reducer;
